import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { AuthState, UserRole } from '@/types/auth';
import type { SignInResult } from './types';
import { auth } from './index';
import { hasBreakGlassToken, clearBreakGlassToken } from './break-glass-auth';

/**
 * Auth context value interface.
 * Erweitert um SRP-Login-Methoden (Spec 0043).
 */
interface AuthContextValue extends AuthState {
  /** Sign in with email and password (SRP direct auth) */
  signIn: (email: string, password: string) => Promise<SignInResult>;
  /** Sign out and clear tokens */
  signOut: () => Promise<void>;
  /** Check if user has required role(s) */
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  /** Refresh current user state */
  refresh: () => Promise<void>;
  /** Initiate password reset (sends verification code) */
  resetPassword: (email: string) => Promise<void>;
  /** Complete password reset with code and new password */
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  /** Complete NEW_PASSWORD_REQUIRED challenge */
  confirmNewPassword: (newPassword: string) => Promise<void>;
  /** Confirm MFA code during login (Spec 0045) */
  confirmMfaCode: (code: string) => Promise<SignInResult>;
  /** Set up TOTP — returns QR code URI and secret key (Spec 0045) */
  setupTotp: () => Promise<{ qrCodeUri: string; secretKey: string }>;
  /** Verify TOTP setup with authenticator code (Spec 0045) */
  verifyTotpSetup: (code: string) => Promise<void>;
  /** Whether current user has completed MFA setup (Spec 0045) */
  mfaSetupComplete: boolean;
}

/**
 * Auth context for React components.
 */
export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Auth provider component that wraps the app.
 * Manages auth state and provides it to children.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    accessToken: null,
  });

  /**
   * Load current auth state.
   */
  const loadAuthState = useCallback(async () => {
    try {
      // Break-Glass Token pruefen (Spec 0044 — Vorrang vor Cognito)
      if (hasBreakGlassToken()) {
        setState({
          user: {
            userId: 'break-glass',
            email: 'break-glass@system',
            role: 'platform_admin',
            merchantId: null,
            mfaSetupComplete: true, // Break-Glass umgeht MFA (Spec 0044/0045)
          },
          isLoading: false,
          isAuthenticated: true,
          accessToken: null, // Break-Glass nutzt sessionStorage, nicht accessToken
        });
        return;
      }

      const [user, accessToken] = await Promise.all([auth.getCurrentUser(), auth.getAccessToken()]);

      setState({
        user,
        isLoading: false,
        isAuthenticated: !!user && !!accessToken,
        accessToken,
      });
    } catch (error) {
      if (import.meta.env.DEV) console.error('Failed to load auth state:', error);
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        accessToken: null,
      });
    }
  }, []);

  /**
   * Sign in with email and password.
   */
  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    return auth.signIn(email, password);
  }, []);

  /**
   * Handle sign out.
   */
  const signOut = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      clearBreakGlassToken(); // Spec 0044: Break-Glass Token loeschen
      await auth.signOut();
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        accessToken: null,
      });
    } catch (error) {
      if (import.meta.env.DEV) console.error('Sign out error:', error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  /**
   * Check if user has required role(s).
   */
  const hasRole = useCallback(
    (roles: UserRole | UserRole[]) => {
      if (!state.user) return false;
      const roleArray = Array.isArray(roles) ? roles : [roles];
      return roleArray.includes(state.user.role);
    },
    [state.user]
  );

  /**
   * Refresh auth state.
   */
  const refresh = useCallback(async () => {
    await loadAuthState();
  }, [loadAuthState]);

  /**
   * Initiate password reset.
   */
  const resetPassword = useCallback(async (email: string) => {
    await auth.resetPassword(email);
  }, []);

  /**
   * Complete password reset.
   */
  const confirmResetPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    await auth.confirmResetPassword(email, code, newPassword);
  }, []);

  /**
   * Complete NEW_PASSWORD_REQUIRED challenge.
   */
  const confirmNewPassword = useCallback(async (newPassword: string) => {
    await auth.confirmNewPassword(newPassword);
  }, []);

  /**
   * Confirm MFA code during SOFTWARE_TOKEN_MFA challenge (Spec 0045).
   */
  const confirmMfaCode = useCallback(async (code: string): Promise<SignInResult> => {
    return auth.confirmMfaCode(code);
  }, []);

  /**
   * Set up TOTP (Spec 0045).
   */
  const setupTotp = useCallback(async () => {
    return auth.setupTotp();
  }, []);

  /**
   * Verify TOTP setup (Spec 0045).
   */
  const verifyTotpSetup = useCallback(async (code: string) => {
    await auth.verifyTotpSetup(code);
  }, []);

  // Initial auth state load
  useEffect(() => {
    loadAuthState();
  }, [loadAuthState]);

  // Subscribe to auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChange(async (user) => {
      if (user) {
        const accessToken = await auth.getAccessToken();
        setState({
          user,
          isLoading: false,
          isAuthenticated: true,
          accessToken,
        });
      } else {
        setState({
          user: null,
          isLoading: false,
          isAuthenticated: false,
          accessToken: null,
        });
      }
    });

    return unsubscribe;
  }, []);

  const value: AuthContextValue = {
    ...state,
    signIn,
    signOut,
    hasRole,
    refresh,
    resetPassword,
    confirmResetPassword,
    confirmNewPassword,
    confirmMfaCode,
    setupTotp,
    verifyTotpSetup,
    mfaSetupComplete: state.user?.mfaSetupComplete ?? false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context.
 * Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
